import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Send, RefreshCw, Bot, UserRound, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "../../lib/api";

export default function AdminChat() {
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const loadSessions = useCallback(() => {
    api.get("/admin/chat/sessions").then((r) => setSessions(r.data)).catch(() => {});
  }, []);

  const loadMessages = useCallback((sid) => {
    if (!sid) return;
    api.get(`/admin/chat/${sid}/messages`)
      .then((r) => {
        setMessages(r.data);
        setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 80);
      })
      .catch(() => {});
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  useEffect(() => {
    const t = setInterval(() => {
      loadSessions();
      if (active) loadMessages(active);
    }, 4000);
    return () => clearInterval(t);
  }, [active, loadSessions, loadMessages]);

  const openSession = (s) => {
    setActive(s.id);
    loadMessages(s.id);
    setTimeout(loadSessions, 500);
  };

  const send = async () => {
    if (!reply.trim() || sending || !active) return;
    setSending(true);
    try {
      await api.post(`/admin/chat/${active}/reply`, { content: reply.trim() });
      setReply("");
      loadMessages(active);
    } catch (e) { toast.error(formatApiError(e)); } finally { setSending(false); }
  };

  const closeSession = async () => {
    try {
      await api.patch(`/admin/chat/${active}`, { status: "closed" });
      toast.success("Chat closed");
      loadSessions();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const reopen = async () => {
    try {
      await api.patch(`/admin/chat/${active}`, { status: "open" });
      toast.success("Chat reopened");
      loadSessions();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const activeSession = sessions.find((s) => s.id === active);

  return (
    <div data-testid="admin-chat-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-brand-emerald">Chat Desk</h1>
          <p className="text-brand-muted text-sm mt-1">Live conversations from the website widget.</p>
        </div>
        <button onClick={loadSessions} data-testid="chat-refresh-btn" className="p-2.5 rounded-xl bg-white border border-[#D6E4DC] text-brand-emerald hover:bg-brand-mint" aria-label="Refresh chats"><RefreshCw className="w-4 h-4" /></button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-2xl border border-[#D6E4DC] overflow-hidden max-h-[70vh] overflow-y-auto" data-testid="chat-sessions-list">
          {sessions.length === 0 ? (
            <p className="p-8 text-center text-brand-muted text-sm">No chat sessions yet.</p>
          ) : sessions.map((s) => (
            <button
              key={s.id}
              data-testid={`chat-session-${s.id.slice(0, 8)}`}
              onClick={() => openSession(s)}
              className={`w-full text-left px-5 py-4 border-b border-[#EFF5F1] hover:bg-brand-mint/60 transition-colors ${active === s.id ? "bg-brand-mint" : ""}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-bold text-sm text-brand-ink truncate flex items-center gap-2">
                  {s.mode === "live" ? <UserRound className="w-3.5 h-3.5 text-brand-gold shrink-0" /> : <Bot className="w-3.5 h-3.5 text-brand-lime shrink-0" />}
                  {s.visitor_name}
                </span>
                <div className="flex items-center gap-1.5 shrink-0">
                  {s.unread_admin > 0 && <span className="bg-red-500 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center" data-testid={`chat-unread-${s.id.slice(0, 8)}`}>{s.unread_admin}</span>}
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${s.status === "open" ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-500"}`}>{s.status}</span>
                </div>
              </div>
              <p className="text-xs text-brand-muted mt-1 truncate">{s.visitor_contact || (s.mode === "live" ? "Live chat" : "Chatting with ProperBot")}</p>
              <p className="text-[10px] text-brand-muted/70 mt-0.5">{new Date(s.updated_at).toLocaleString("en-NG")}</p>
            </button>
          ))}
        </div>

        <div className="lg:col-span-2 bg-white rounded-2xl border border-[#D6E4DC] flex flex-col max-h-[70vh]" data-testid="chat-conversation-panel">
          {active ? (
            <>
              <div className="px-5 py-4 border-b border-[#EFF5F1] flex items-center justify-between">
                <div>
                  <p className="font-bold text-brand-ink text-sm">{activeSession?.visitor_name}</p>
                  <p className="text-xs text-brand-muted">{activeSession?.visitor_contact || "No contact shared"} · {activeSession?.mode === "live" ? "Live" : "Bot mode"}</p>
                </div>
                {activeSession?.status === "open" ? (
                  <button onClick={closeSession} data-testid="chat-close-btn" className="text-xs font-bold text-red-500 hover:text-red-700 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Close chat</button>
                ) : (
                  <button onClick={reopen} data-testid="chat-reopen-btn" className="text-xs font-bold text-brand-emerald hover:text-brand-lime">Reopen</button>
                )}
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-3 bg-brand-canvas min-h-[300px]" data-testid="admin-chat-messages">
                {messages.map((m) => (
                  <div key={m.id} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                      m.sender === "admin" ? "bg-brand-emerald text-white rounded-br-md"
                      : m.sender === "visitor" ? "bg-white border border-[#D6E4DC] text-brand-ink rounded-bl-md shadow-sm"
                      : "bg-brand-gold/10 border border-brand-gold/30 text-brand-ink rounded-bl-md"
                    }`}>
                      {m.sender === "bot" && <p className="text-[10px] font-bold text-brand-gold uppercase tracking-wider mb-0.5 flex items-center gap-1"><Bot className="w-3 h-3" /> ProperBot</p>}
                      {m.content}
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>
              <div className="p-4 border-t border-[#EFF5F1] flex gap-2">
                <input
                  data-testid="admin-chat-reply-input"
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Reply to visitor…"
                  className="flex-1 text-sm px-4 py-2.5 rounded-full border border-[#D6E4DC] focus:outline-none focus:ring-2 focus:ring-brand-lime/50"
                />
                <button onClick={send} disabled={sending || !reply.trim()} data-testid="admin-chat-send-btn" className="w-10 h-10 rounded-full bg-brand-emerald text-white flex items-center justify-center hover:bg-brand-lime disabled:opacity-40 transition-colors" aria-label="Send reply">
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-brand-muted text-sm p-10" data-testid="chat-no-selection">
              Select a conversation to view messages and reply.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
